/**
 * LocationSelector Component
 *
 * Displays customer's practice locations with day-of-week availability
 * Used in Step 2 of the multi-physician booking flow
 */

import React, { useState, useEffect } from 'react';
import { customerLocationService } from '../services/locationService';
import { logger } from '../utils/logger';
import { MapPin, Plus, Building2, Clock } from 'lucide-react';

const LocationSelector = ({
  customerId,
  selectedLocation,
  onSelectLocation,
  onAddNewLocation
}) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoading(true);
        setError(null);
        const customerLocations = await customerLocationService.getCustomerLocations(customerId);
        setLocations(customerLocations);
      } catch (err) {
        logger.error('Error loading locations', { error: err.message, customerId });
        setError('Failed to load practice locations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (customerId) {
      loadLocations();
    }
  }, [customerId]);

  const getDayBadges = (location) => {
    const days = [
      { key: 'works_monday', label: 'Mon' },
      { key: 'works_tuesday', label: 'Tue' },
      { key: 'works_wednesday', label: 'Wed' },
      { key: 'works_thursday', label: 'Thu' },
      { key: 'works_friday', label: 'Fri' },
      { key: 'works_saturday', label: 'Sat' },
      { key: 'works_sunday', label: 'Sun' }
    ];

    return days.filter(day => location[day.key]).map(day => day.label);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading practice locations...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Reload page
        </button>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Practice Locations</h3>
        <p className="text-gray-600 mb-6">
          Add a practice location to schedule office lunches.
        </p>
        <button
          onClick={onAddNewLocation}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Practice Location
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Select Practice Location</h2>
          <p className="text-sm text-gray-600 mt-1">
            Choose where the office lunch will be held
          </p>
        </div>
        <button
          onClick={onAddNewLocation}
          className="inline-flex items-center px-3 py-2 text-sm bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <Plus className="w-4 h-4 mr-1" />
          Add Location
        </button>
      </div>

      <div className="space-y-3">
        {locations.map((location) => {
          // Handle both formats: {id: ...} and {location_id: ...}
          const selectedId = selectedLocation?.id || selectedLocation?.location_id;
          const isSelected = selectedId === location.id;
          const practiceLocation = location.practice_locations;
          const availableDays = getDayBadges(location);

          return (
            <div
              key={location.id}
              onClick={() => onSelectLocation(location)}
              className={`
                relative p-4 border-2 rounded-lg cursor-pointer transition-all
                ${isSelected
                  ? 'border-blue-600 bg-blue-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                }
              `}
            >
              <div className="flex items-start">
                {/* Radio button */}
                <div className="flex items-center h-5 mt-1">
                  <input
                    type="radio"
                    checked={isSelected}
                    onChange={() => onSelectLocation(location)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                </div>

                {/* Location details */}
                <div className="ml-3 flex-1">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center">
                        <h3 className="text-base font-semibold text-gray-900">
                          {practiceLocation?.location_name || 'Unnamed Location'}
                        </h3>
                        {location.is_primary_location && (
                          <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                            Primary
                          </span>
                        )}
                      </div>

                      {practiceLocation?.address && (
                        <div className="flex items-start mt-1 text-sm text-gray-600">
                          <MapPin className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" />
                          <span>
                            {practiceLocation.address}
                            {practiceLocation.city && `, ${practiceLocation.city}`}
                            {practiceLocation.state && `, ${practiceLocation.state}`}
                            {practiceLocation.zip_code && ` ${practiceLocation.zip_code}`}
                          </span>
                        </div>
                      )}

                      {practiceLocation?.location_type && (
                        <div className="mt-1 text-sm text-gray-500">
                          <Building2 className="w-4 h-4 inline mr-1" />
                          {practiceLocation.location_type.charAt(0).toUpperCase() +
                           practiceLocation.location_type.slice(1)}
                        </div>
                      )}

                      {/* Day of week availability */}
                      {availableDays.length > 0 && (
                        <div className="flex items-center mt-2">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="flex flex-wrap gap-1">
                            {availableDays.map((day) => (
                              <span
                                key={day}
                                className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded"
                              >
                                {day}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {availableDays.length === 0 && (
                        <div className="mt-2 text-sm text-amber-600">
                          <Clock className="w-4 h-4 inline mr-1" />
                          No availability set
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Helper text */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Tip:</strong> The calendar will show available dates based on the location's
          day-of-week settings and existing meeting schedule.
        </p>
      </div>
    </div>
  );
};

export default LocationSelector;
